import React, { useContext, useEffect, useMemo, useState } from "react";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import CircularProgress from "@mui/material/CircularProgress";
import { debounce } from "@mui/material";
import MainContext from "../../context/mainContext";
import { useTranslation } from "react-i18next";

interface SelectPickerProps<T> {
  id: string;
  placeholder?: string | undefined;
  defaultValue: T | null;
  readOnly: boolean;
  error?: string | string[] | false | undefined;
  disabled?: boolean | undefined;
  onChange: (newValue: T | null) => void;
  onBlur: (event: any) => void;
  search: (query: string) => Promise<T[]>;
  getOptionLabel: (option: T) => string;
  isOptionEqualToValue: (option: T, value: T) => boolean;
}

const MIN_LENGTH = 3;
const DEBOUNCE_MS = 400;

function _selectPicker<T extends any>({
  id,
  defaultValue,
  placeholder,
  readOnly,
  error,
  disabled,
  onChange,
  onBlur,
  search,
  getOptionLabel,
  isOptionEqualToValue,
}: SelectPickerProps<T>) {
  const { t } = useTranslation();
  const [options, setOptions] = useState<readonly T[]>([]);
  const [value, setValue] = useState<T | null>(null);
  const [inputValue, setInputValue] = useState("");
  const [searching, setSearching] = useState(false);

  const { service } = useContext(MainContext);

  const fetchData = useMemo(
    () =>
      debounce((request: { query: string }, callback: (results?: readonly T[]) => void) => {
        setSearching(true);
        service(() => search(request.query), {
          callback: callback,
          loader: setSearching,
        });
      }, DEBOUNCE_MS),
    [service, search]
  );

  const handleBlur = (event: any) => {
    if (onBlur) onBlur(event);
  };

  useEffect(() => {
    let active = true;

    if (inputValue === "" || inputValue.length < MIN_LENGTH) {
      setOptions(value ? [value] : []);
      return undefined;
    }

    setSearching(true);

    fetchData({ query: inputValue }, (results?: readonly T[]) => {
      if (active) {
        let newOptions: readonly T[] = [];

        if (value) {
          newOptions = [value];
        }

        if (results) {
          newOptions = [...newOptions, ...results];
        }

        setOptions(newOptions);
      }
    });

    return () => {
      active = false;
    };
  }, [value, inputValue, fetchData]);

  useEffect(() => {
    let active = true;

    if (active) setValue(defaultValue);

    return () => {
      active = false;
    };
  }, [defaultValue]);

  return (
    <Autocomplete
      id={id}
      size="small"
      getOptionLabel={(option) => (typeof option === "string" ? option : getOptionLabel(option))}
      filterOptions={(x) => x}
      isOptionEqualToValue={isOptionEqualToValue}
      options={options}
      placeholder={placeholder}
      autoComplete
      includeInputInList
      filterSelectedOptions
      readOnly={readOnly}
      value={value}
      disabled={disabled}
      noOptionsText={inputValue?.length < MIN_LENGTH || searching ? t("enterAtLeast3Characters") : t("noMatches")}
      onChange={(event: any, newValue: T | null) => {
        setOptions(newValue ? [newValue, ...options] : options);
        setValue(newValue);
        onChange(newValue);
      }}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue);
      }}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          placeholder={placeholder}
          helperText={error}
          error={Boolean(error)}
          onBlur={handleBlur}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {searching ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}

const GenericSelectPicker = React.memo(_selectPicker) as typeof _selectPicker;

export default GenericSelectPicker;
